<template>
    <page-container>
        <page-header> Outbound Messages </page-header>
        <div class="flex flex-col gap-6 xl:flex-row">
            <div class="shrink-0 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    @reset="resetFilters()"
                    @applyResources="applyResources"
                    @applySnippets="applySnippets"
                    :minAllowedDate="minAllowedDate"
                ></analytics-sidebar>
            </div>

            <div class="w-full relative">
                <div
                    class="absolute w-full transition z-2"
                    :class="{
                        'opacity-0 pointer-events-none translate-y-20': !selectingResources,
                    }"
                >
                    <analytics-select-resources
                        ref="selectResourcesRef"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        v-model="fetchParams.filters.resources"
                        :fetchParams="fetchParams"
                    >
                    </analytics-select-resources>
                </div>

                <div
                    class="absolute w-full transition z-2"
                    :class="{
                        'opacity-0 pointer-events-none translate-y-20': !selectingSnippets,
                    }"
                >
                    <analytics-select-snippets
                        ref="selectSnippetsRef"
                        v-model="fetchParams.filters.snippet_ids"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="fetchParams"
                    />
                </div>

                <transition
                    enter-from-class="opacity-0 -translate-y-4"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-4"
                >
                    <div v-if="showLoadingBars" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    class="absolute w-full transition z-1"
                    :class="{
                        'opacity-0 pointer-events-none':
                            showLoadingBars || messagesSent || selectingResources || selectingSnippets,
                    }"
                >
                    <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                        <div class="mx-auto text-center">No messages match your current filters.</div>
                        <vx-button type="button" color="primary" size="lg" @click="resetFilters">
                            Reset Filters
                            <font-awesome-icon widthAuto :icon="faArrowRotateLeft" />
                        </vx-button>
                    </vx-box>
                </div>

                <div
                    v-if="debouncedFetchParams"
                    class="grid gap-6 grid-cols-1 sm:grid-cols-12"
                    :class="{
                        'opacity-0 pointer-events-none':
                            showLoadingBars || !messagesSent || selectingResources || selectingSnippets,
                        'opacity-100 delay-200 transition duration-150': !(
                            showLoadingBars ||
                            !messagesSent ||
                            selectingResources ||
                            selectingSnippets
                        ),
                    }"
                >
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        resourceType="user"
                        minHeight="128px"
                        class="col-span-1 sm:col-span-6 lg:col-span-3"
                    />
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        resourceType="campaign"
                        minHeight="128px"
                        class="col-span-1 sm:col-span-6 lg:col-span-3"
                    />
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        resourceType="automation"
                        minHeight="128px"
                        class="col-span-1 sm:col-span-6 lg:col-span-3"
                    />

                    <analytics-outbound-messages-total-messages-sent-by-audience-type
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        class="col-span-1 sm:col-span-12"
                    />

                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        ref="messagesSentChart"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />
                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        messageType="sms"
                        :class="cardsColSize"
                    />
                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        messageType="mms"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-total-contacts-messaged
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-delivery-rate
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-opt-out-rate
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-unique-click-through-rate
                        v-if="showUniqueClickThroughRate"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-click-through-rate
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        :class="cardsColSize"
                    />

                    <analytics-outbound-messages-message-reply-rate
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        class="col-span-1 sm:col-span-12"
                        :class="[showUniqueClickThroughRate ? 'lg:col-span-4' : 'lg:col-span-3']"
                    />

                    <analytics-outbound-messages-audience-origin-table
                        v-if="showAudienceOriginTable"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        filename="outbound-messages-audience-origin-metrics"
                        :onlyCampaign="true"
                        class="col-span-1 sm:col-span-12"
                    />
                    <analytics-outbound-messages-export-resource-metrics
                        v-if="showExportResourceMetricsTable"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :fetchParams="debouncedFetchParams"
                        filename="outbound-messages-export-resource-metrics"
                        class="col-span-1 sm:col-span-12"
                    />
                    <template v-else-if="!fetchParams.filters.resources?.length">
                        <analytics-outbound-messages-total-messages-sent-by-resource
                            @resource:click="setResource"
                            :orgUnitId="props.orgUnitId"
                            :groupId="props.groupId"
                            :fetchParams="debouncedFetchParams"
                            class="col-span-1 sm:col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            :rateType="showUniqueClickThroughRate ? 'unique_click_through_rate' : 'click_through_rate'"
                            :orgUnitId="props.orgUnitId"
                            :groupId="props.groupId"
                            :fetchParams="debouncedFetchParams"
                            class="col-span-1 sm:col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            rateType="message_reply_rate"
                            :orgUnitId="props.orgUnitId"
                            :groupId="props.groupId"
                            :fetchParams="debouncedFetchParams"
                            class="col-span-1 sm:col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            rateType="opt_out_rate"
                            :orgUnitId="props.orgUnitId"
                            :groupId="props.groupId"
                            :fetchParams="debouncedFetchParams"
                            class="col-span-1 sm:col-span-6"
                        />
                    </template>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { computed, ref } from 'vue';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingBars from '../general/LoadingBars.vue';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import AnalyticsSelectResources from '~/components/analytics/partial/select-resources/AnalyticsSelectResources.vue';
import AnalyticsOutboundMessagesTotalActiveResourcesByResourceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalActiveResourcesByResourceType.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByAudienceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByAudienceType.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByMessageType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByMessageType.vue';
import AnalyticsOutboundMessagesDeliveryRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesDeliveryRate.vue';
import AnalyticsOutboundMessagesUniqueClickThroughRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesUniqueClickThroughRate.vue';
import AnalyticsOutboundMessagesClickThroughRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesClickThroughRate.vue';
import AnalyticsOutboundMessagesMessageReplyRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesMessageReplyRate.vue';
import AnalyticsOutboundMessagesOptOutRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesOptOutRate.vue';
import AnalyticsOutboundMessagesRateTypeByResource from './charts/time-series/AnalyticsOutboundMessagesRateTypeByResource.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByResource from './charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByResource.vue';
import AnalyticsOutboundMessagesAudienceOriginTable from './charts/time-series/AnalyticsOutboundMessagesAudienceOriginTable.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import {
    getDateRange,
    getMinAllowedDate,
    isCampaign,
    minUniqueClickThroughRateDate,
    resourceTypesOptions,
} from '~/utils/analytics';
import { audienceTypes } from '~/constants/campaign';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { useAnalyticsSelectResources } from '~/components/analytics/partial/select-resources/useAnalyticsSelectResources';
import { useAnalyticsSelectSnippets } from '~/components/analytics/partial/select-snippets/useAnalyticsSelectSnippets';
import AnalyticsSelectSnippets from '~/components/analytics/partial/select-snippets/AnalyticsSelectSnippets.vue';
import AnalyticsOutboundMessagesExportResourceMetrics from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesExportResourceMetrics.vue';

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');
    const filters = {
        sender_types: SENDER_TYPES.map((senderType) => senderType.value),
        audience_types: audienceTypes().map((audienceType) => audienceType.name),
        resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
        resource_categories: undefined,
        resources: undefined,
        snippet_ids: undefined,
    };

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: filters,
    };
};

const { fetchParams, debouncedFetchParams, loading, resetFilters } = useDebouncedFetchParams(defaultFilters());
const { selectingResources } = useAnalyticsSelectResources();
const { selectingSnippets } = useAnalyticsSelectSnippets();

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const minAllowedDate = computed(() => getMinAllowedDate(Boolean(props.groupId), Boolean(props.orgUnitId)));

const showUniqueClickThroughRate = computed(() => minUniqueClickThroughRateDate.isBefore(fetchParams.range[0]));

const cardsColSize = computed(() => {
    if (showUniqueClickThroughRate.value) {
        return 'col-span-1 sm:col-span-6 lg:col-span-4';
    }
    return 'col-span-1 sm:col-span-6 lg:col-span-3';
});

/**
 * @param {MouseEvent} event
 */
const setResource = (event, resource) => {
    if (event.metaKey || event.ctrlKey) {
        return;
    }

    event.preventDefault();
    fetchParams.filters.resources = resource
        ? [{ resource_type: resource.resource_type, resource_id: resource.resource_id }]
        : undefined;
};

const showActiveResourcesByResourceType = computed(() => {
    return (
        !(
            fetchParams.filters.resource_types?.length === 1 && fetchParams.filters.resource_types?.includes?.('other')
        ) && !fetchParams.filters.resources?.length
    );
});

const showAudienceOriginTable = computed(() => {
    return debouncedFetchParams.value.filters.resources?.some((resource) => isCampaign(resource.resource_type));
});

const showExportResourceMetricsTable = computed(() => {
    return (
        debouncedFetchParams.value.filters.resources?.length &&
        debouncedFetchParams.value.filters.resources.length >= 1 &&
        debouncedFetchParams.value.filters.resources.length <= 10
    );
});

const messagesSentChart = ref();
const messagesSent = computed(() => !messagesSentChart.value?.loading && messagesSentChart.value?.value);
const showLoadingBars = computed(() => loading.value || messagesSentChart.value?.loading);

const selectResourcesRef = ref();
const selectSnippetsRef = ref();

const applyResources = () => {
    selectResourcesRef.value?.apply();
};

const applySnippets = () => {
    selectSnippetsRef.value?.apply();
};
</script>
